import React, { useState, useEffect } from 'react';
import { useGlobal } from 'reactn';
import numeral from 'numeral';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { get } from 'lodash';
import { Statistic, Button } from 'antd';
import Tag from '../../common/components/Tag';
import TimesheetListActions from './TimesheetListActions';
import { CANCEL_TIMESHEET_TIMESHEET_LINEITEM } from '../../project-manager/mutations';
import { GET_TIMESHEETS_FOR_USER } from '../queries';
import TimesheetLoading from './TimesheetLoading';
import classNames from 'classnames';
import { DELETE_TIMESHEET } from '../mutations';
import TimesheetApprovers from '../../project-manager/components/TimesheetApprovers';
import { getColorByStatus, getCurrentWeekEndDate, getWeekEndDayForDate } from '../../common';

const mapStateToProps = state => ({
  user: state.meteor.user,
});

const TimesheetListContainer = ({ startDate, endDate, setRange, onLoadingChange }) => {
  const [global] = useGlobal();
  const weekEndDay = get(global, 'config.["week-end-day"]');
  const weekEndDayChange = get(global, 'config.["week-end-day-change"]');
  const history = useHistory();
  const [months, setMonths] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const { loading, data, refetch } = useQuery(GET_TIMESHEETS_FOR_USER, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
  });

  const timesheets = get(data, 'me.timesheets');

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);
  const [cancelTimesheet] = useMutation(CANCEL_TIMESHEET_TIMESHEET_LINEITEM, {
    refetchQueries: [{ query: GET_TIMESHEETS_FOR_USER, variables: { startDate, endDate } }],
    awaitRefetchQueries: true,
  });
  const [deleteTimesheet] = useMutation(DELETE_TIMESHEET, {
    refetchQueries: [{ query: GET_TIMESHEETS_FOR_USER, variables: { startDate, endDate } }],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    // Only process when data has finished loading and timesheets are available
    if (timesheets && !loading) {
      const startMoment = moment(startDate, 'YYYYMMDD');
      const endMoment = moment(endDate, 'YYYYMMDD');
      const numOfMonths = endMoment.diff(startMoment, 'months') + 1;
      const _months = [...new Array(numOfMonths)].map((_, index) => {
        const monthEndMoment = new moment(endMoment).subtract(index, 'month');
        const year = monthEndMoment.format('YYYY');
        const number = monthEndMoment.format('MM');
        const weeks = [];

        const mapToMoment = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

        // Find first week end date of the month
        const monthStart = moment().year(year).month(Number(number) - 1).date(1);
        
        const findNextWeekEnd = (date) => {
            let d = date.clone();
             for(let i=0; i<14; i++) {
                const weDay = getWeekEndDayForDate(d);
                if (d.day() === mapToMoment[weDay]) {
                     return d;
                }
                d.add(1, 'day');
             }
             return d;
        }

        let currentWeekEnd = findNextWeekEnd(monthStart);
        
        const daysInMonth = monthEndMoment.daysInMonth();
        const lastDayOfMonth = moment().year(year).month(Number(number) - 1).date(daysInMonth);
        
        const monthWeeks = [];
        let safety = 0;

        while(currentWeekEnd.isSameOrBefore(lastDayOfMonth) || (currentWeekEnd.month() === lastDayOfMonth.month() && currentWeekEnd.year() === lastDayOfMonth.year())) {
             const endDate = currentWeekEnd.format('YYYYMMDD');
             const currentDate = currentWeekEnd.clone();
             
             const findPrevWeekEnd = (date) => {
                 let d = date.clone().subtract(1, 'day');
                 for(let i=0; i<14; i++) {
                    const weDay = getWeekEndDayForDate(d);
                    if (d.day() === mapToMoment[weDay]) {
                         return d;
                    }
                    d.subtract(1, 'day');
                 }
                 return d;
            }
            const prevWeekEnd = findPrevWeekEnd(currentWeekEnd);
            const weekStart = prevWeekEnd.clone().add(1, 'day');

            const foundTimesheet = timesheets.find(t => {
                  if (t.endDate === endDate) return true;
                  const tEndDate = moment(t.endDate, 'YYYYMMDD');
                  return tEndDate.isBetween(weekStart, currentDate, 'day', '[]');
             });

             monthWeeks.push({
                  endDate: foundTimesheet ? foundTimesheet.endDate : endDate,
                  timesheet: foundTimesheet,
             });

             currentWeekEnd = findNextWeekEnd(currentWeekEnd.clone().add(1, 'day'));
             safety++;
             if (safety > 6) break; // Max 6 weeks per month
             
             if (currentWeekEnd.isAfter(lastDayOfMonth) && currentWeekEnd.month() !== lastDayOfMonth.month()) {
                 break;
             }
        }
        
        weeks.push(...monthWeeks);

        return {
          name: monthEndMoment.format('MMMM'),
          number,
          year,
          weeks: weeks.sort((a, b) => b.endDate - a.endDate),
        };
      });
      setMonths(_months);
      setInitialLoaded(true);
    }
  }, [timesheets, endDate, startDate, weekEndDay, weekEndDayChange, loading]);

  // Load More (past) Function
  const loadMore = () => {
    const newMomentStartDate = moment(startDate, 'YYYYMMDD').subtract(1, 'month');
    setRange({
      startDate: newMomentStartDate,
      endDate: moment(endDate, 'YYYYMMDD'),
    });
  };

  if (loading && !initialLoaded) {
    return <TimesheetLoading />;
  }

  // Calculate running card index for staggered animation
  let cardIndex = 0;

  return (
    <div>
      {months &&
        months.map(({ year, name, weeks }, index) => {
          return (
            <div key={index}>
              <div className="text-2xl font-medium mb-2">{`${name} ${year}`}</div>
              <div className="flex flex-wrap mb-4">
                {weeks.map(week => {
                  const currentCardIndex = cardIndex++;
                  const currentWeek = getCurrentWeekEndDate() === week.endDate;
                  const overdue = moment(week.endDate, 'YYYYMMDD').isBefore(moment(), 'day');

                  if (week.timesheet) {
                    const { timesheet } = week;
                    const { _id, endDate, status, totalHours, approvalsRequired } = timesheet;
                    return (
                      <div
                        key={_id}
                        className="p-1 w-full md:w-1/2 lg:w-1/3 opacity-0 animate-fade-in"
                        style={{ animationDelay: `${currentCardIndex * 50}ms` }}
                      >
                        <div
                          className={classNames(
                            'relative bg-white p-4 rounded-[5px] h-64 hover:shadow-lg shadow-md transition-shadow duration-200 overflow-hidden',
                            // {
                            //   'border-[1px] border-solid border-gray-400': !currentWeek,
                            //   'border-[1px] border-gray-500 border-solid': currentWeek,
                            // },
                          )}
                        >
                          {currentWeek && (
                            <div className="absolute bottom-0 left-0 p-1 bg-gray-500 text-xs text-white rounded-tr-[5px]">
                              Current Week
                            </div>
                          )}
                          {overdue && timesheet.status !== 'Approved' && (
                            <div className="absolute bottom-0 right-0 p-1 bg-red-200 text-xs text-white rounded-tl-[5px]">
                              Overdue
                            </div>
                          )}
                          <div className="flex justify-between">
                            <div>
                              <Tag color={getColorByStatus(status)}>{status}</Tag>
                            </div>
                            <TimesheetListActions
                              timesheet={timesheet}
                              cancel={() =>
                                cancelTimesheet({
                                  variables: { timesheetId: _id },
                                })
                              }
                              deleteTimesheet={() => {
                                deleteTimesheet({
                                  variables: { timesheetId: _id },
                                });
                              }}
                              refetch={refetch}
                            />
                          </div>
                          <div className="flex justify-between">
                            <div className="text-lg text-gray-800">
                              {moment(endDate, 'YYYYMMDD').format('LL')}
                            </div>
                          </div>
                          <div className="flex justify-between py-2">
                            <Statistic
                              title="Total Hours"
                              value={numeral(totalHours).format('0.[00]')}
                            />
                            <div>
                              <div className="">
                                {approvalsRequired?.length === 0 ? (
                                  <Statistic
                                    title="Approvals Needed"
                                    value={
                                      approvalsRequired.length === 0
                                        ? 'None'
                                        : approvalsRequired.filter(a => a.approvedBy).length
                                    }
                                    suffix={
                                      approvalsRequired.length === 0
                                        ? null
                                        : `/ ${approvalsRequired.length}`
                                    }
                                  />
                                ) : (
                                  <div>
                                    <div
                                      className="text-right"
                                      style={{ color: 'rgba(0, 0, 0, 0.45)' }}
                                    >
                                      Approvals Needed
                                    </div>
                                    <div className="flex flex-row flex-wrap justify-end gap-2 my-3">
                                      <TimesheetApprovers approvalsRequired={approvalsRequired} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* <Statistic
                              title="Approvals Needed"
                              value={
                                approvalsRequired.length === 0 ? 'None' : approvalsRequired.filter(a => a.approvedBy).length
                              }
                              suffix={
                                approvalsRequired.length === 0
                                  ? null
                                  : `/ ${approvalsRequired.length}`
                              }
                            /> */}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={week.endDate}
                      className="p-1 w-full md:w-1/2 lg:w-1/3 opacity-0 animate-fade-in"
                      style={{ animationDelay: `${currentCardIndex * 50}ms` }}
                    >
                      <div
                        className={classNames(
                          'relative p-4 rounded-md h-64 shadow-inner bg-gray-200 overflow-hidden',
                          {
                            'border-[1px] border-dashed border-gray-400': !currentWeek,
                            'border-[1px] border-gray-500 border-solid': currentWeek,
                          },
                        )}
                      >
                        {currentWeek && (
                          <div className="absolute bottom-0 left-0 p-1 bg-gray-500 text-xs text-white rounded-tr-md">
                            Current Week
                          </div>
                        )}
                        {overdue && (
                          <div className="absolute bottom-0 right-0 p-1 bg-red-200 text-xs text-white rounded-tl-md">
                            Overdue
                          </div>
                        )}
                        <div className="text-lg text-gray-800">
                          {moment(week.endDate, 'YYYYMMDD').format('LL')}
                        </div>
                        <div className="h-full flex items-center flex-col">
                          <div className="text-base text-gray-700 mb-4 mt-12">
                            No timesheet exists for this week
                          </div>
                          <Button
                            type="primary"
                            disabled={loading}
                            onClick={() => history.push(`/user/timesheets/${week.endDate}`)}
                          >
                            Open
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      {initialLoaded && (
        <div className="flex justify-center mt-4">
          <Button loading={loading} onClick={loadMore}>
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};

export default connect(mapStateToProps)(TimesheetListContainer);
